import {
  appendClickIdsToLink,
  buildPaidAdjustArgs,
  getCampaignAttribution,
  sanitizePathForAdjust
} from './campaign-attribution.js';

// SEOENG-117 (DAR-235): every app-download exit carries the page that sent the visitor, as the
// Adjust adgroup, so a website signup can be credited to a page.
//
// The generated organic deep links already do this (campaign Download_banner_btn, adgroup = the
// page path, see useIdOrganicAdjustLink). Every button that falls back to a static Adjust link did
// not: the cara-beli-saham CTAs, the ticker buy card, the news download section, the footer store
// badges, the /app-download redirects and the download modal's QR all sent visitors out with no
// adgroup, so those signups reached Adjust with no page at all.
//
// The same campaign name as the generated organic link, so both land in one Adjust campaign with
// the page as the adgroup. Only added when the link has no campaign of its own: Adjust's campaign
// structure is campaign > adgroup > creative, and an adgroup without a campaign is not recorded.
export const ORGANIC_WEBSITE_CAMPAIGN = 'Download_banner_btn';

// The download modal's QR used a static image of heygotradeid.go.link/QRCode, whose tracker is
// adj_t=1x3e1ori. A static image cannot carry the page, so the modal now renders a live QR of the
// same tracker with the page tag (scans keep their tracker and gain the page).
export const DOWNLOAD_MODAL_QR_TRACKER_LINK = 'https://heygotradeid.go.link?adj_t=1x3e1ori';

// SEOENG-125: the GTG modal's QR. GTG has no dedicated QR tracker, so its QR uses the GTG default
// tracker (1mgrtz2l), page-tagged like every other exit; a scan lands in the same GTG Website
// bucket as a CTA tap, credited to its page. A dedicated GTG QR tracker is a one-line swap here.
export const GTG_DOWNLOAD_MODAL_QR_TRACKER_LINK = 'https://heygotrade.go.link?adj_t=1mgrtz2l';

// The modal QR tracker for the page's Adjust app kind ('gti' | 'gtg'); anything that is not GTI
// resolves to GTG, matching getCurrentAdjustAppKind's default.
export function downloadModalQrTrackerLink(appKind) {
  return appKind === 'gti' ? DOWNLOAD_MODAL_QR_TRACKER_LINK : GTG_DOWNLOAD_MODAL_QR_TRACKER_LINK;
}

// DAR-235 follow-up (Joseph, conv 88672): which button sent the visitor, as the Adjust creative.
// The header, hero and sticky download buttons generated one identical organic link (same
// campaign, same page adgroup, so one cache key and one Adjust link), so a signup could be credited
// to a page but never to a button. Each placement now carries its own creative: the generated
// links pass it to the deep-link API, and static tracker links gain adj_creative=<placement>.
// Closed vocabulary: an unknown placement adds nothing, so a typo cannot mint a new Adjust creative.
// SEOENG-167 (Ocin, conv 98316): two exits that carried the page but no placement get their own
// value, so a read of the placement mix can tell them apart: app_download_redirect is the mobile
// auto-redirect to the store on the app-download pages (the biggest single exit), and body is
// every other download CTA (in-body sections, promo CTAs, the ticker buy card), the default for a
// download CTA that names none of the four scoped buttons.
export const ADJUST_PLACEMENTS = Object.freeze({
  HEADER: 'header',
  HERO: 'hero',
  STICKY: 'sticky',
  FOOTER: 'footer',
  BODY: 'body',
  APP_DOWNLOAD_REDIRECT: 'app_download_redirect'
});
const PLACEMENT_VALUES = new Set(Object.values(ADJUST_PLACEMENTS));

export function normalizeAdjustPlacement(placement) {
  return typeof placement === 'string' && PLACEMENT_VALUES.has(placement) ? placement : '';
}

// The placement a register-CTA ctaLocation stands for, so callers that already label their CTA
// for GA4 (SEOENG-56) do not have to say it twice. Other locations have no placement.
const PLACEMENT_BY_CTA_LOCATION = Object.freeze({
  header: ADJUST_PLACEMENTS.HEADER,
  hero: ADJUST_PLACEMENTS.HERO,
  mobile_download_bar: ADJUST_PLACEMENTS.STICKY
});

export function placementForCtaLocation(ctaLocation) {
  return (ctaLocation && PLACEMENT_BY_CTA_LOCATION[ctaLocation]) || '';
}

// The placement a download CTA sends (SEOENG-167): the one it names, else the one its ctaLocation
// stands for, else body. Every generated download link and every download-modal tap goes through
// this, so no download CTA leaves the site without a creative.
export function downloadCtaPlacement(placement, ctaLocation = null) {
  return normalizeAdjustPlacement(placement) || placementForCtaLocation(ctaLocation) || ADJUST_PLACEMENTS.BODY;
}

// The attribute a container sets so the plain store-badge anchors inside it are tagged with its
// placement (the footer badges are plain <a href> links with no click handler of ours).
export const PLACEMENT_ATTR = 'data-adj-placement';

const ADJUST_LINK_HOST = /(^|\.)go\.link$/i;

// A link this helper may extend: an Adjust link host (*.go.link) with its tracker token in the
// query (adj_t). Short links (go.link/<slug>) resolve on Adjust's side to a URL the page cannot
// read, and the generated ones already carry their adgroup, so they are left exactly as they are.
export function isAdjustTrackerLink(link) {
  if (!link || typeof link !== 'string') return false;
  let url;
  try {
    url = new URL(link);
  } catch {
    return false;
  }
  return ADJUST_LINK_HOST.test(url.hostname) && Boolean(url.searchParams.get('adj_t'));
}

// Appends adj_adgroup=<page path> (and adj_campaign when the link has none, and adj_creative=
// <placement> when a known placement is given and the link has no creative) to an Adjust tracker
// link. Returns the link unchanged when it is not an Adjust tracker link, already has an adgroup
// (a paid link's utm_content, a campaign link's own adgroup), or there is no page path. The
// existing query is never re-encoded: the new params are appended as text, ahead of any fragment,
// the same way appendClickIdsToLink adds click ids.
export function tagAdjustLinkWithPage(link, pathname, placement = '') {
  if (!isAdjustTrackerLink(link)) return link;
  const adgroup = sanitizePathForAdjust(typeof pathname === 'string' ? pathname : '');
  if (!adgroup) return link;

  const { searchParams } = new URL(link);
  if (searchParams.has('adj_adgroup')) return link;

  const params = [];
  if (!searchParams.get('adj_campaign')) {
    params.push(`adj_campaign=${encodeURIComponent(ORGANIC_WEBSITE_CAMPAIGN)}`);
  }
  params.push(`adj_adgroup=${encodeURIComponent(adgroup)}`);
  const creative = normalizeAdjustPlacement(placement);
  if (creative && !searchParams.get('adj_creative')) {
    params.push(`adj_creative=${encodeURIComponent(creative)}`);
  }

  return appendAdjustParams(link, params);
}

// Appends already-encoded name=value pairs to a link as text, ahead of any fragment, without
// re-encoding the existing query.
function appendAdjustParams(link, params) {
  const hashAt = link.indexOf('#');
  const base = hashAt === -1 ? link : link.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : link.slice(hashAt);
  const separator = base.includes('?') ? (/[?&]$/.test(base) ? '' : '&') : '?';
  return `${base}${separator}${params.join('&')}${hash}`;
}

// DAR-235 follow-up (Jessica, conv 87870): on a visit with campaign params, a static tracker link
// used to go out untagged, so Adjust filed the click under the tracker's own name
// (1vm9jsh8 = Website::Download_banner_btn) with an empty adgroup and creative: a third of the
// October website download clickers, all on that one token. The static link now carries the same
// campaign structure the generated paid link sends for this visit (buildPaidAdjustArgs):
// adj_campaign=<channel>_<utm_campaign>, adj_adgroup=<utm_content or the page>, adj_creative=
// <utm_term> when there is one. The campaign always starts with the paid channel, so a paid click
// is never relabelled as the organic Download_banner_btn campaign (the SEOENG-38 rule). A link
// that already carries its own adj_campaign or adj_adgroup is left exactly as it is.
//
// SEOENG-172 round 2 (Jessica, DAR-331): the GTI static tokens are campaign- or adgroup-level
// trackers, and Adjust keeps a tracker's own campaign. On 1vm9jsh8 (Website::Download_banner_btn)
// a click sent with adj_campaign=paid_brokerpage&adj_adgroup=openaccount was filed under the bare
// tracker with an empty adgroup (2 of 2 clicks before the fix, 2 of 2 after); 1m6f4dae
// (gotradeindo::GTI Website QR) keeps its own campaign and takes only the adgroup. A paid campaign
// can only open a sub-tracker under the network-level Website token 1mem6vgy, which is the token
// the generated paid links already use (paid_brokerpage, youtube_*, gdn_*, sem_* all land there).
// So on a paid visit the static GTI token is swapped for 1mem6vgy; the fallback and redirect
// params are kept as they are, so the visitor still lands in the same store.
export const GTI_WEBSITE_NETWORK_TRACKER = '1mem6vgy';
const GTI_STATIC_TRACKERS_WITHOUT_PAID_CAMPAIGNS = new Set(['1vm9jsh8', '1m6f4dae', '1x3e1ori']);
const GTI_ADJUST_HOST = /^heygotradeid\.go\.link$/i;

function withPaidCapableTracker(link) {
  const { hostname, searchParams } = new URL(link);
  const token = searchParams.get('adj_t');
  if (!GTI_ADJUST_HOST.test(hostname) || !GTI_STATIC_TRACKERS_WITHOUT_PAID_CAMPAIGNS.has(token)) return link;
  return link.replace(new RegExp(`([?&])adj_t=${token}(?=[&#]|$)`), `$1adj_t=${GTI_WEBSITE_NETWORK_TRACKER}`);
}

export function tagAdjustLinkWithPaidArgs(link, paidArgs) {
  if (!isAdjustTrackerLink(link) || !paidArgs || !paidArgs.campaign) return link;
  const { searchParams } = new URL(link);
  if (searchParams.get('adj_campaign') || searchParams.has('adj_adgroup')) return link;

  const params = [`adj_campaign=${encodeURIComponent(paidArgs.campaign)}`];
  if (paidArgs.adgroup) params.push(`adj_adgroup=${encodeURIComponent(paidArgs.adgroup)}`);
  if (paidArgs.creative && !searchParams.get('adj_creative')) {
    params.push(`adj_creative=${encodeURIComponent(paidArgs.creative)}`);
  }
  return appendAdjustParams(withPaidCapableTracker(link), params);
}

// The page to tag a download exit with, or '' when this visit must not be page-tagged. A paid
// visit (utm_* / gclid in the URL or kept for the session, SEOENG-38) is left exactly as before:
// its campaign and adgroup come from the ad (adgroup = utm_content), and stamping the organic
// campaign onto a paid click would relabel a paid install as organic in Adjust.
export function pageTagPathForCurrentVisit({ attribution } = {}) {
  if (typeof window === 'undefined') return '';
  const paid = attribution === undefined ? getCampaignAttribution() : attribution;
  return paid ? '' : window.location.pathname;
}

// The paid argument set for this visit (buildPaidAdjustArgs), or null on an organic visit.
export function paidAdjustArgsForCurrentVisit({ attribution } = {}) {
  if (typeof window === 'undefined') return null;
  const paid = attribution === undefined ? getCampaignAttribution() : attribution;
  return paid ? buildPaidAdjustArgs(paid, { path: window.location.pathname }) : null;
}

// Tags a download exit for this visit: the page (and placement) on an organic visit, the visit's
// paid campaign structure on a visit with campaign params.
export function tagAdjustLinkForVisit(link, placement = '', { attribution } = {}) {
  if (typeof window === 'undefined') return link;
  const paid = attribution === undefined ? getCampaignAttribution() : attribution;
  if (paid) {
    // The Google click ids ride the static link too, as they do on the generated paid link, so
    // Adjust can hand the conversion back to Google Ads (appendClickIdsToLink never duplicates).
    const tagged = tagAdjustLinkWithPaidArgs(link, buildPaidAdjustArgs(paid, { path: window.location.pathname }));
    return tagged === link ? link : appendClickIdsToLink(tagged, paid);
  }
  return tagAdjustLinkWithPage(link, window.location.pathname, placement);
}

export function tagAdjustLinkWithCurrentPage(link, placement = '') {
  return tagAdjustLinkForVisit(link, placement);
}

// SEOENG-186 (Jessica, DAR-263): QR_Code installs that reached Adjust with no creative. The QR
// tracker 1x3e1ori is an adgroup-level tracker (Download_banner_btn > QR_Code), so Adjust keeps
// its own campaign and adgroup and takes only the creative from the link. The modal's live QR
// sent adj_creative (body), but every place that still showed the static QR image
// (the desktop sticky QR card on /id/ and the welcome-rewards
// pages, the /id/app-download page, the campaign download template, a modal opened with no QR
// link) sent the bare tracker, so those scans had no creative. Each of them now renders a live QR
// of this link instead.

// Adds adj_creative=<placement> to an Adjust tracker link that has none. A link that is not an
// Adjust tracker link, already names a creative, or is given no known placement is unchanged.
export function withAdjustCreative(link, placement) {
  if (!isAdjustTrackerLink(link)) return link;
  const creative = normalizeAdjustPlacement(placement);
  if (!creative || new URL(link).searchParams.get('adj_creative')) return link;
  return appendAdjustParams(link, [`adj_creative=${encodeURIComponent(creative)}`]);
}

// The link a download QR encodes for this visit: the app's QR tracker, tagged for the visit like
// the modal QR (the page and placement on an organic visit, the paid structure on a paid one).
// When the visit adds nothing (server render, no page path), it still carries the placement as
// its creative, so no QR the site renders leaves without one. The placement defaults to body.
export function downloadQrLinkForVisit(appKind = 'gti', placement = '', { attribution } = {}) {
  const tracker = downloadModalQrTrackerLink(appKind);
  const creative = normalizeAdjustPlacement(placement) || ADJUST_PLACEMENTS.BODY;
  const tagged = tagAdjustLinkForVisit(tracker, creative, { attribution });
  return tagged === tracker ? withAdjustCreative(tracker, creative) : tagged;
}

// Plain <a href="https://…go.link?adj_t=…"> links (the footer store badges, the SEM primary CTA
// while its deep link resolves, links inside CMS copy) never pass through a click handler we own,
// so they are tagged at the moment of interaction instead: pointerdown / click / auxclick /
// contextmenu / focusin cover a tap, a click, a middle click, a long press or right click (copy
// link) and keyboard focus before Enter. The browser reads href only after the event, so the
// tagged URL is the one it follows.
//
// Gatsby can keep an anchor's DOM node across a client-side navigation between two pages of the
// same template, and React only rewrites href when the prop changes. So the untagged original is
// remembered on the anchor and re-tagged from on every interaction: a link tagged on page A is
// re-tagged for page B instead of keeping A's adgroup.
const ORIGINAL_HREF_ATTR = 'data-adj-original-href';
const TAGGED_HREF_ATTR = 'data-adj-tagged-href';

// The placement of the nearest container marked with data-adj-placement, or ''.
export function placementForAnchor(anchor) {
  const holder = anchor && typeof anchor.closest === 'function' ? anchor.closest(`[${PLACEMENT_ATTR}]`) : null;
  return holder ? normalizeAdjustPlacement(holder.getAttribute(PLACEMENT_ATTR)) : '';
}

export function tagAnchorWithPage(anchor, pathname, placement = placementForAnchor(anchor)) {
  retagAnchor(anchor, (base) => tagAdjustLinkWithPage(base, pathname, placement));
}

// The anchor tagging for this visit: the page on an organic visit, the paid campaign structure
// on a visit with campaign params (see tagAdjustLinkWithPaidArgs).
export function tagAnchorForVisit(anchor, { attribution } = {}) {
  const placement = placementForAnchor(anchor);
  retagAnchor(anchor, (base) => tagAdjustLinkForVisit(base, placement, { attribution }));
}

function retagAnchor(anchor, tag) {
  if (!anchor || typeof anchor.getAttribute !== 'function') return;
  const current = anchor.getAttribute('href');
  if (!current) return;

  // Still our own earlier write (React has not replaced it): start again from the original.
  const base =
    anchor.getAttribute(TAGGED_HREF_ATTR) === current
      ? anchor.getAttribute(ORIGINAL_HREF_ATTR) || current
      : current;
  const tagged = tag(base);
  if (tagged === base) return;

  anchor.setAttribute(ORIGINAL_HREF_ATTR, base);
  anchor.setAttribute(TAGGED_HREF_ATTR, tagged);
  if (tagged !== current) anchor.setAttribute('href', tagged);
}

export const ANCHOR_TAG_EVENTS = Object.freeze([
  'pointerdown',
  'click',
  'auxclick',
  'contextmenu',
  'focusin'
]);

export function installAdjustAnchorPageTagging(doc) {
  const target = doc || (typeof document !== 'undefined' ? document : null);
  if (!target) return () => {};

  const handler = (event) => {
    const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (anchor) tagAnchorForVisit(anchor);
  };
  ANCHOR_TAG_EVENTS.forEach((type) => target.addEventListener(type, handler, true));
  return () => ANCHOR_TAG_EVENTS.forEach((type) => target.removeEventListener(type, handler, true));
}
